import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CalendarDays, DollarSign, CheckCircle, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

interface Campaign {
  id: string;
  name: string;
  description?: string;
  status: string;
  start_date?: string;
  end_date?: string;
  budget: number;
  created_at: string;
}

interface Deal {
  id: string;
  title: string;
  deal_value: number;
  status: string;
  created_at: string;
  campaign_id?: string;
}

interface PaymentRecord {
  id: string;
  milestone_name: string;
  amount: number;
  due_date: string;
  status: string;
  payment_date?: string;
}

interface CampaignHistoryModalProps {
  athleteId: string;
  athleteName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const CampaignHistoryModal: React.FC<CampaignHistoryModalProps> = ({
  athleteId,
  athleteName,
  isOpen,
  onClose
}) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useUser();

  const fetchCampaignHistory = async () => {
    if (!currentUser?.id || !athleteId) return;

    setLoading(true);
    try {
      // Fetch deals for this athlete
      const { data: dealsData, error: dealsError } = await supabase
        .from('deals')
        .select('*')
        .eq('talent_profile_id', athleteId)
        .order('created_at', { ascending: false });

      if (dealsError) throw dealsError;
      setDeals(dealsData || []);

      // Fetch campaigns for these deals
      if (dealsData?.length > 0) {
        const campaignIds = dealsData
          .map(deal => deal.campaign_id)
          .filter(Boolean);

        if (campaignIds.length > 0) {
          const { data: campaignsData, error: campaignsError } = await supabase
            .from('campaigns')
            .select('*')
            .in('id', campaignIds)
            .order('created_at', { ascending: false });

          if (campaignsError) throw campaignsError;
          setCampaigns(campaignsData || []);
        }

        // Fetch payment schedules for these deals
        const dealIds = dealsData.map(deal => deal.id);
        const { data: paymentsData, error: paymentsError } = await supabase
          .from('payment_schedules')
          .select('*')
          .in('deal_id', dealIds)
          .order('due_date', { ascending: false });

        if (paymentsError) throw paymentsError;
        setPayments(paymentsData || []);
      }
    } catch (error) {
      console.error('Error fetching campaign history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCampaignHistory();
    }
  }, [isOpen, athleteId, currentUser?.id]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalDealsValue = deals.reduce((sum, deal) => sum + (deal.deal_value || 0), 0);
  const totalPaid = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
  const totalPending = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Campaign History - {athleteName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Total Deal Value
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(totalDealsValue)}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Total Paid
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(totalPaid)}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Pending Payments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-amber-600">{formatCurrency(totalPending)}</div>
                </CardContent>
              </Card>
            </div>

            <Tabs defaultValue="deals" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="deals">Deals ({deals.length})</TabsTrigger>
                <TabsTrigger value="campaigns">Campaigns ({campaigns.length})</TabsTrigger>
                <TabsTrigger value="payments">Payments ({payments.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="deals" className="space-y-4">
                {deals.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No deals found for this athlete.
                  </div>
                ) : (
                  deals.map((deal) => (
                    <Card key={deal.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{deal.title}</CardTitle>
                            <p className="text-sm text-muted-foreground">
                              Created {formatDate(deal.created_at)}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-semibold">{formatCurrency(deal.deal_value)}</div>
                            <Badge className={getStatusColor(deal.status)}>
                              {deal.status}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="campaigns" className="space-y-4">
                {campaigns.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No campaigns found for this athlete.
                  </div>
                ) : (
                  campaigns.map((campaign) => (
                    <Card key={campaign.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{campaign.name}</CardTitle>
                            {campaign.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {campaign.description}
                              </p>
                            )}
                            <p className="text-sm text-muted-foreground mt-2">
                              {campaign.start_date && campaign.end_date 
                                ? `${formatDate(campaign.start_date)} - ${formatDate(campaign.end_date)}`
                                : `Created ${formatDate(campaign.created_at)}`
                              }
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-semibold">{formatCurrency(campaign.budget)}</div>
                            <Badge className={getStatusColor(campaign.status)}>
                              {campaign.status}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="payments" className="space-y-4">
                {payments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No payment records found for this athlete.
                  </div>
                ) : (
                  payments.map((payment) => (
                    <Card key={payment.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{payment.milestone_name}</CardTitle>
                            <p className="text-sm text-muted-foreground">
                              Due: {formatDate(payment.due_date)}
                              {payment.payment_date && (
                                <span className="ml-2">• Paid: {formatDate(payment.payment_date)}</span>
                              )}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-semibold">{formatCurrency(payment.amount)}</div>
                            <Badge className={getStatusColor(payment.status)}>
                              {payment.status}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};